import get from 'lodash/get';
import omit from 'lodash/omit';
import map from 'lodash/map';
import isEmpty from 'lodash/isEmpty';
import { createReducer } from '@reduxjs/toolkit';

import {
  ActionFailureType,
  ActionSuccessType,
  CONFIRM_USER,
  CURRENT_USER,
  SIGN_IN,
  SIGN_OUT,
  USER_RESET_PASSWORD,
  USER_SPECIFIC_SITE,
  USER_JOIN_AS_MEMBER,
  USER_JOIN_AS_EXPERT,
  ActionClearType,
  SUBMIT_ONBOARDING,
  VISIT_ONBOARDING_STEP,
  COMPLETE_REGISTRATION,
  UPDATE_PAYOUT_METHOD,
  GET_USER_PHONES_FOR_SITE,
  VERIFY_USER_PHONE,
  CREATE_PHONE_VERIFICATION,
  UPDATE_CURRENT_USER_AVATAR,
} from 'constants/redux-actions';

const initState = {};

const omitUserSpecificSiteAttributes = (prevState) =>
  omit(prevState, [
    'loadedUserSpecificSite',
    'assistedSignup',
    'isExpert',
    'isMember',
    'expertProfileId',
    'slug',
    'pendingApproval',
    'onboarding',
    'registrationChecklist',
    'pendingExpertApproval',
  ]);

const updatePhoneNumber = (prevState, action) => {
  const updatedPhone = action.payload.data;
  const phoneNumbers = get(prevState, 'phoneNumbers', []);

  return {
    ...prevState,
    phoneNumbers: map(phoneNumbers, (phone) =>
      phone.twilioSupportedCountryId === updatedPhone.twilioSupportedCountryId ? { ...phone, ...updatedPhone } : phone,
    ),
  };
};

const userReducer = createReducer(initState, {
  [ActionSuccessType(SIGN_IN)]: (prevState, action) => ({
    ...prevState,
    ...omit(get(action, 'payload.data', {}), 'token'),
  }),
  [ActionSuccessType(CURRENT_USER)]: (prevState, action) => ({
    ...prevState,
    ...get(action, 'payload.data', {}),
  }),
  [ActionSuccessType(USER_SPECIFIC_SITE)]: (prevState, action) => ({
    ...prevState,
    loadedUserSpecificSite: true,
    ...get(action, 'payload.data', {}),
  }),
  [ActionFailureType(USER_SPECIFIC_SITE)]: omitUserSpecificSiteAttributes,
  [ActionClearType(USER_SPECIFIC_SITE)]: omitUserSpecificSiteAttributes,
  [ActionFailureType(CURRENT_USER)]: () => initState,
  [ActionSuccessType(CONFIRM_USER)]: (prevState, action) => ({
    ...prevState,
    ...omit(get(action, 'payload.data', {}), 'token'),
  }),
  [ActionSuccessType(USER_RESET_PASSWORD)]: (prevState, action) => ({
    ...prevState,
    ...omit(get(action, 'payload.data', {}), 'token'),
  }),
  [ActionSuccessType(USER_JOIN_AS_MEMBER)]: (prevState) => ({ ...prevState, isMember: true }),
  [ActionSuccessType(USER_JOIN_AS_EXPERT)]: (prevState) => ({ ...prevState, isMember: true, isExpert: true }),
  [UPDATE_CURRENT_USER_AVATAR]: (prevState, action) => ({
    ...prevState,
    avatarUrl: get(action, 'payload.avatarUrl', null),
  }),
  [ActionSuccessType(SUBMIT_ONBOARDING)]: (prevState) => ({ ...prevState, pendingApproval: true }),
  [ActionFailureType(SUBMIT_ONBOARDING)]: (prevState) => ({ ...prevState, pendingApproval: false }),
  [ActionSuccessType(VISIT_ONBOARDING_STEP)]: (prevState, action) => ({
    ...prevState,
    registrationChecklist: get(action, 'payload.data', {}),
  }),
  [ActionSuccessType(COMPLETE_REGISTRATION)]: (prevState, action) => ({
    ...prevState,
    ...get(action, 'payload.data', {}),
  }),
  [SIGN_OUT]: () => initState,
  [ActionSuccessType(UPDATE_PAYOUT_METHOD)]: (prevState, action) => ({
    ...prevState,
    payable: get(action.payload.data, 'payable'),
    invalidStripeAccount: !isEmpty(get(action.payload.data, 'stripeAccountRequirementsErrors')),
  }),
  [ActionSuccessType(GET_USER_PHONES_FOR_SITE)]: (prevState, action) => ({
    ...prevState,
    phoneNumbers: get(action, 'payload.data', []),
  }),
  [ActionSuccessType(CREATE_PHONE_VERIFICATION)]: updatePhoneNumber,
  [ActionSuccessType(VERIFY_USER_PHONE)]: updatePhoneNumber,
});

export default userReducer;
